import { useEffect, useState } from 'react'
import { apiClient } from '@/lib/api'
import { usePermissions } from '@/hooks/usePermission'

type Inspection = { id: string; type: string; result: 'PASS' | 'FAIL' | 'PENDING'; note: string | null; createdAt: string }

export function InspectionsModule({ jobId, fixture = false }: { jobId: string; fixture?: boolean }) {
  return <JobInspections key={`${jobId}:${fixture}`} jobId={jobId} fixture={fixture} />
}

// Each job owns its draft and pending requests. A response from an unmounted
// job can never update the next job's state.
function JobInspections({ jobId, fixture }: { jobId: string; fixture: boolean }) {
  const [items, setItems] = useState<Inspection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [type, setType] = useState('')
  const [result, setResult] = useState<Inspection['result']>('PASS')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const permissions = usePermissions()
  useEffect(() => {
    if (fixture) {
      setItems([{ id: 'demo-inspection', type: 'Framing', result: 'PENDING', note: '[DEMO] Follow-up scheduled', createdAt: '2026-10-01T12:00:00Z' }])
      setLoading(false)
      return
    }
    let active = true
    setLoading(true); setError(''); setItems([])
    apiClient.get(`/api/jobs/${jobId}/inspections`).then(res => { if (active) setItems(res.data.data) })
      .catch(() => { if (active) setError('Could not load inspections.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [jobId, fixture])
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError('')
    try {
      if (fixture) {
        setItems(prev => [{ id: `demo-${Date.now()}`, type: type.trim(), result, note: note.trim(), createdAt: new Date().toISOString() }, ...prev])
        setType(''); setNote(''); setResult('PASS')
        return
      }
      const res = await apiClient.post(`/api/jobs/${jobId}/inspections`, { type: type.trim(), result, note: note.trim() })
      setItems(prev => [res.data.data, ...prev]); setType(''); setNote(''); setResult('PASS')
    } catch { setError('Could not save inspection. Please try again.') } finally { setSaving(false) }
  }
  return <section className="ds-tile min-w-0 p-4 space-y-3" aria-label="Inspections">
    <h3 className="font-semibold">Inspections</h3>
    {fixture && <p>Design fixture — changes are temporary.</p>}
    {loading ? <p>Loading inspections…</p> : error && items.length === 0 ? null : items.length === 0 ? <p>No inspections logged yet.</p> :
      <ul className="space-y-2">{items.map(item => <li key={item.id} className="ds-tile-inset min-w-0 break-words p-3">
        <p>{item.type} · {item.result.toLowerCase()}</p>
        <p>{new Date(item.createdAt).toLocaleDateString()}</p>{item.note && <p className="whitespace-pre-wrap break-words">{item.note}</p>}
      </li>)}</ul>}
    {error && <p role="alert">{error}</p>}
    {(fixture || permissions.can('jobs.edit') || permissions.can('jobs.complete')) && <form onSubmit={submit} className="space-y-2">
      <label className="block">Inspection type<input className="input-field w-full min-w-0" value={type} onChange={e => setType(e.target.value)} maxLength={120} required /></label>
      <label className="block">Result<select className="input-field w-full min-w-0" value={result} onChange={e => setResult(e.target.value as Inspection['result'])}><option value="PASS">Pass</option><option value="FAIL">Fail</option><option value="PENDING">Pending</option></select></label>
      <label className="block">Note (optional)<textarea className="input-field w-full min-w-0" value={note} onChange={e => setNote(e.target.value)} maxLength={2000} /></label>
      <button className="btn-primary" disabled={saving || !type.trim()}>{saving ? 'Saving…' : 'Log inspection'}</button>
    </form>}
  </section>
}
